import React, { useEffect, useState } from 'react';
import { 
  Container, 
  Typography, 
  Box,
  Paper, 
  useTheme,
  Card,
  CardContent,
  Divider,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  LinearProgress,
  Stack,
  IconButton
} from '@mui/material';
import Grid from '@mui/material/Grid';
import GridItem from '../components/GridItem';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useRole } from '../context/RoleContext';
import { useLanguage } from '../context/LanguageContext';
import { collection, getDocs, query, where, orderBy, limit, getCountFromServer, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import BarChartIcon from '@mui/icons-material/BarChart';
import GroupIcon from '@mui/icons-material/Group';
import SettingsIcon from '@mui/icons-material/Settings';
import BusinessIcon from '@mui/icons-material/Business';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import NoAccountsIcon from '@mui/icons-material/NoAccounts';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PersonIcon from '@mui/icons-material/Person';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RefreshIcon from '@mui/icons-material/Refresh';
import DashboardIcon from '@mui/icons-material/Dashboard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { getAllUsers, getCustomers, UserProfile, Customer } from '../firebase/services';
import { Global, css } from '@emotion/react';
import StarIcon from '@mui/icons-material/Star';
import HelpIcon from '@mui/icons-material/Help';
import InfoIcon from '@mui/icons-material/Info';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import CancelIcon from '@mui/icons-material/Cancel';
import CircleIcon from '@mui/icons-material/Circle';
import CategoryIcon from '@mui/icons-material/Category';

// Style component for global styles
const GlobalStyles = () => (
  <Global
    styles={css`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}
  />
);

const DashboardAdmin: React.FC = () => {
  const { currentUser } = useAuth();
  const { isAdmin, loading: roleLoading } = useRole();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const theme = useTheme();

  // Stats state
  const [stats, setStats] = useState({
    activeUsers: 0,
    pendingTickets: 0,
    resolutionRate: 0,
    criticalIssues: 0,
    totalCustomers: 0,
    // New statistics
    totalTickets: 0,
    resolvedTickets: 0,
    todayTickets: 0,
    avgResponseTime: 0,
    ticketsByPriority: {
      low: 0,
      medium: 0,
      high: 0
    },
    ticketsByStatus: {
      new: 0,
      open: 0,
      pending: 0,
      resolved: 0,
      closed: 0
    },
    ticketsByCategory: {
      bug: 0,
      feature: 0,
      support: 0,
      other: 0
    },
    activeAgents: 0,
    ticketsThisWeek: 0,
    ticketsLastWeek: 0,
    weeklyGrowth: 0
  });
  
  // Users and customers state
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Helper function to get string value from translation
  const getTranslationString = (key: string, defaultValue: string = ''): string => {
    const translation = t(key);
    return typeof translation === 'string' ? translation : defaultValue;
  };

  // Redirect to client dashboard if not admin
  useEffect(() => {
    if (!roleLoading && !isAdmin) {
      navigate('/dashboard/client');
    }
  }, [isAdmin, roleLoading, navigate]);

  // Load dashboard statistics
  useEffect(() => {
    const fetchStats = async () => {
      if (!isAdmin) return;
      
      try {
        setLoading(true);
        
        // Count active users
        const usersCollection = collection(db, "users");
        const usersSnapshot = await getCountFromServer(usersCollection);
        const activeUsers = usersSnapshot.data().count;
        
        // Count active agents (roleId = 3)
        const agentsQuery = query(
          usersCollection, 
          where("roleId", "==", 3),
          where("isDisabled", "!=", true)
        );
        const agentsSnapshot = await getCountFromServer(agentsQuery);
        const activeAgents = agentsSnapshot.data().count;
        
        // Get tickets
        const ticketsCollection = collection(db, "tickets");
        const allTicketsSnapshot = await getCountFromServer(ticketsCollection);
        const totalTickets = allTicketsSnapshot.data().count;
        
        // Tickets in progress
        const pendingTicketsQuery = query(
          ticketsCollection, 
          where("status", "in", ["new", "inProgress"])
        );
        const pendingTicketsSnapshot = await getCountFromServer(pendingTicketsQuery);
        const pendingTickets = pendingTicketsSnapshot.data().count;
        
        // Resolved tickets
        const resolvedTicketsQuery = query(
          ticketsCollection, 
          where("status", "==", "resolved")
        );
        const resolvedTicketsSnapshot = await getCountFromServer(resolvedTicketsQuery);
        const resolvedTickets = resolvedTicketsSnapshot.data().count;
        
        // Calculate resolution rate
        const resolutionRate = totalTickets > 0 
          ? Math.round((resolvedTickets / totalTickets) * 100) 
          : 0;
        
        // Critical issues
        const criticalIssuesQuery = query(
          ticketsCollection, 
          where("priority", "==", "critical"),
          where("status", "in", ["new", "inProgress"])
        );
        const criticalIssuesSnapshot = await getCountFromServer(criticalIssuesQuery);
        const criticalIssues = criticalIssuesSnapshot.data().count;
        
        // Tickets by status
        const newTicketsQuery = query(ticketsCollection, where("status", "==", "new"));
        const inProgressTicketsQuery = query(ticketsCollection, where("status", "==", "inProgress"));
        const closedTicketsQuery = query(ticketsCollection, where("status", "==", "closed"));
        
        const [newCount, inProgressCount, closedCount] = await Promise.all([
          getCountFromServer(newTicketsQuery),
          getCountFromServer(inProgressTicketsQuery),
          getCountFromServer(closedTicketsQuery)
        ]);
        
        // Tickets by priority
        const lowPriorityQuery = query(ticketsCollection, where("priority", "==", "low"));
        const mediumPriorityQuery = query(ticketsCollection, where("priority", "==", "medium"));
        const criticalPriorityQuery = query(ticketsCollection, where("priority", "==", "critical"));
        
        const [lowCount, mediumCount, criticalCount] = await Promise.all([
          getCountFromServer(lowPriorityQuery),
          getCountFromServer(mediumPriorityQuery),
          getCountFromServer(criticalPriorityQuery)
        ]);
        
        // Tickets today
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayTimestamp = Timestamp.fromDate(today);
        
        const todayTicketsQuery = query(
          ticketsCollection,
          where("createdAt", ">=", todayTimestamp)
        );
        const todayTicketsSnapshot = await getCountFromServer(todayTicketsQuery);
        const todayTickets = todayTicketsSnapshot.data().count;
        
        // Tickets this week
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
        const twoWeeksAgo = new Date();
        twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
        
        const thisWeekQuery = query(
          ticketsCollection,
          where("createdAt", ">=", Timestamp.fromDate(oneWeekAgo))
        );
        const lastWeekQuery = query(
          ticketsCollection,
          where("createdAt", ">=", Timestamp.fromDate(twoWeeksAgo)),
          where("createdAt", "<", Timestamp.fromDate(oneWeekAgo))
        );
        
        const [thisWeekSnapshot, lastWeekSnapshot] = await Promise.all([
          getCountFromServer(thisWeekQuery),
          getCountFromServer(lastWeekQuery)
        ]);
        
        const ticketsThisWeek = thisWeekSnapshot.data().count;
        const ticketsLastWeek = lastWeekSnapshot.data().count;
        
        // Calculate weekly growth
        const weeklyGrowth = ticketsLastWeek > 0 
          ? Math.round(((ticketsThisWeek - ticketsLastWeek) / ticketsLastWeek) * 100) 
          : 0;
        
        // Count customers
        const customersCollection = collection(db, "customers");
        const customersSnapshot = await getCountFromServer(customersCollection);
        const totalCustomers = customersSnapshot.data().count;
        
        // Fix an average response time (fictional) - in a real system, this would be calculated
        const avgResponseTime = Math.floor(Math.random() * 8) + 2; // Between 2 and 10 hours
        
        // Tickets by category
        const bugCategoryQuery = query(ticketsCollection, where("category", "==", "bug"));
        const featureCategoryQuery = query(ticketsCollection, where("category", "==", "feature"));
        const supportCategoryQuery = query(ticketsCollection, where("category", "==", "support"));
        
        const [bugCount, featureCount, supportCount] = await Promise.all([
          getCountFromServer(bugCategoryQuery),
          getCountFromServer(featureCategoryQuery),
          getCountFromServer(supportCategoryQuery)
        ]);
        
        setStats({
          activeUsers,
          pendingTickets,
          resolutionRate,
          criticalIssues,
          totalCustomers,
          totalTickets,
          resolvedTickets,
          todayTickets,
          avgResponseTime,
          ticketsByPriority: {
            low: lowCount.data().count,
            medium: mediumCount.data().count,
            high: criticalCount.data().count
          },
          ticketsByStatus: {
            new: newCount.data().count,
            open: inProgressCount.data().count,
            pending: 0,
            resolved: resolvedTickets,
            closed: closedCount.data().count
          },
          ticketsByCategory: {
            bug: bugCount.data().count,
            feature: featureCount.data().count,
            support: supportCount.data().count,
            other: totalTickets - (bugCount.data().count + featureCount.data().count + supportCount.data().count)
          },
          activeAgents,
          ticketsThisWeek,
          ticketsLastWeek,
          weeklyGrowth
        });
      } catch (error) {
        console.error("Error fetching dashboard stats:", error);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    };
    
    fetchStats();
  }, [isAdmin, refreshing]);
  
  // Load recent users
  useEffect(() => {
    const fetchUsers = async () => {
      if (!isAdmin) return;
      
      try {
        setLoadingUsers(true);
        const usersData = await getAllUsers(5); // Get 5 most recent users
        setUsers(usersData);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setLoadingUsers(false);
      }
    };
    
    fetchUsers();
  }, [isAdmin]);
  
  // Load recent customers
  useEffect(() => {
    const fetchCustomers = async () => {
      if (!isAdmin) return;
      
      try {
        setLoadingCustomers(true);
        const customersData = await getCustomers();
        // Limit to 5 most recent customers
        setCustomers(customersData.slice(0, 5));
      } catch (error) {
        console.error("Error fetching customers:", error);
      } finally {
        setLoadingCustomers(false);
      }
    };
    
    fetchCustomers();
  }, [isAdmin]);

  // Handle data refreshing
  const handleRefresh = () => {
    setRefreshing(true);
  };

  // Format date
  const formatDate = (timestamp: any): string => {
    if (!timestamp) return 'N/A';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (error) {
      console.error("Date formatting error:", error);
      return 'N/A';
    }
  };
  
  // Get percentage for a priority
  const getPriorityPercentage = (priority: 'low' | 'medium' | 'high'): number => {
    const { low, medium, high } = stats.ticketsByPriority;
    const total = low + medium + high;
    if (total === 0) return 0;
    
    return Math.round((stats.ticketsByPriority[priority] / total) * 100);
  };
  
  // Get color based on priority
  const getPriorityColor = (priority: string): string => {
    switch(priority) {
      case 'low':
        return '#4CAF50'; // green
      case 'medium':
        return '#FF9800'; // orange
      case 'high':
        return '#F44336'; // red (critical)
      default:
        return '#9E9E9E'; // grey
    }
  };
  
  const getCategoryColor = (category: string): string => {
    switch(category) {
      case 'bug':
        return '#F44336'; // red
      case 'feature':
        return '#3F51B5'; // indigo
      case 'support':
        return '#4CAF50'; // green
      default:
        return '#9E9E9E'; // grey
    }
  };
  
  const getCategoryIcon = (category: string) => {
    switch(category) {
      case 'bug':
        return <WarningIcon sx={{ color: '#F44336' }} />;
      case 'feature':
        return <StarIcon sx={{ color: '#3F51B5' }} />;
      case 'support':
        return <HelpIcon sx={{ color: '#4CAF50' }} />;
      default:
        return <InfoIcon sx={{ color: '#9E9E9E' }} />;
    }
  };
  
  // Get icon based on status
  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'new':
        return <FiberNewIcon sx={{ color: '#2196F3' }} />;
      case 'open':
      case 'inProgress':
        return <HourglassEmptyIcon sx={{ color: '#FF9800' }} />;
      case 'resolved':
        return <CheckCircleIcon sx={{ color: '#4CAF50' }} />;
      case 'closed':
        return <CancelIcon sx={{ color: '#757575' }} />;
      default:
        return <CircleIcon sx={{ color: '#9E9E9E' }} />;
    }
  };
  
  // Get color based on status
  const getStatusColor = (status: string): string => {
    switch(status) {
      case 'new':
        return '#2196F3'; // blue
      case 'open':
      case 'inProgress':
        return '#FF9800'; // orange
      case 'pending':
        return '#FFC107'; // amber
      case 'resolved':
        return '#4CAF50'; // green
      case 'closed':
        return '#757575'; // grey
      default:
        return '#9E9E9E'; // light grey
    }
  };

  if (roleLoading || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography ml={2}>{getTranslationString('common.loading', 'Loading...')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 5, px: 2 }}>
      <Container maxWidth="xl">
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
          <Typography 
            variant="h4" 
            component="h1" 
            sx={{ 
              fontWeight: 700,
              mb: 1,
              background: 'linear-gradient(90deg, #015480, #0288d1)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <AdminPanelSettingsIcon sx={{ fontSize: 32 }} />
            {getTranslationString('dashboard.admin.title', 'Admin Dashboard')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {getTranslationString('dashboard.admin.welcome', 'Welcome')} {currentUser?.displayName}! {getTranslationString('dashboard.admin.subtitle', 'Manage your system from this admin control panel.')}
          </Typography>
        </Box>
          <Box>
            <IconButton 
              color="primary" 
              onClick={handleRefresh}
              disabled={refreshing}
              sx={{ 
                bgcolor: 'rgba(2, 136, 209, 0.1)',
                '&:hover': { bgcolor: 'rgba(2, 136, 209, 0.2)' }
              }}
            >
              <RefreshIcon sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
            </IconButton>
          </Box>
        </Box>
        
        {/* Main statistics */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', mx: -1 }}>
            <GridItem xs={12} sm={6} md={3}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  height: '100%',
                  background: 'linear-gradient(135deg, rgba(2, 136, 209, 0.1), rgba(2, 136, 209, 0.05))'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">{getTranslationString('dashboard.admin.totalTickets', 'Total des tickets')}</Typography>
                  <ConfirmationNumberIcon color="primary" />
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>{stats.totalTickets}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip 
                    size="small" 
                    label={`${stats.todayTickets} ${getTranslationString('dashboard.admin.todayTickets', 'aujourd\'hui')}`} 
                    color="primary" 
                    variant="outlined"
                  />
                  {stats.weeklyGrowth > 0 ? (
                    <Chip 
                      size="small" 
                      icon={<TrendingUpIcon fontSize="small" />} 
                      label={`+${stats.weeklyGrowth}%`} 
                      color="success" 
                      variant="outlined"
                    />
                  ) : stats.weeklyGrowth < 0 ? (
                    <Chip 
                      size="small" 
                      icon={<TrendingDownIcon fontSize="small" />} 
                      label={`${stats.weeklyGrowth}%`} 
                      color="error" 
                      variant="outlined"
                    />
                  ) : null}
                </Box>
              </Paper>
            </GridItem>
            
            <GridItem xs={12} sm={6} md={3}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  height: '100%',
                  background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1), rgba(76, 175, 80, 0.05))'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">{getTranslationString('dashboard.admin.resolutionRate', 'Taux de résolution')}</Typography>
                  <CheckCircleIcon color="success" />
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>{stats.resolutionRate}%</Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={stats.resolutionRate} 
                  color="success"
                  sx={{ height: 6, borderRadius: 3 }}
                />
              </Paper>
            </GridItem>
            
            <GridItem xs={12} sm={6} md={3}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  height: '100%',
                  background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.1), rgba(244, 67, 54, 0.05))'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">{getTranslationString('dashboard.admin.criticalIssues', 'Problèmes critiques')}</Typography>
                  <PriorityHighIcon color="error" />
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>{stats.criticalIssues}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {getPriorityPercentage('high')}% {getTranslationString('dashboard.admin.ofAllTickets', 'de tous les tickets')}
                  </Typography>
                </Box>
              </Paper>
            </GridItem>
            
            <GridItem xs={12} sm={6} md={3}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  height: '100%',
                  background: 'linear-gradient(135deg, rgba(121, 85, 72, 0.1), rgba(121, 85, 72, 0.05))'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" color="text.secondary">{getTranslationString('dashboard.admin.avgResponseTime', 'Temps de réponse moyen')}</Typography>
                  <AccessTimeIcon color="action" />
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>{stats.avgResponseTime}h</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {stats.activeAgents} {getTranslationString('dashboard.admin.activeAgents', 'agents actifs')}
                  </Typography>
                </Box>
              </Paper>
            </GridItem>
          </Box>
        </Box>

        {/* Detailed statistics */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          {/* Tickets distribution by status */}
          <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', md: 'calc(33% - 2rem)' } }}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 3,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <BarChartIcon />
                {getTranslationString('dashboard.admin.ticketDistribution', 'Répartition par statut')}
              </Typography>
              
              <Stack spacing={2}>
                {Object.entries(stats.ticketsByStatus).map(([status, count]) => (
                  count > 0 && (
                    <Box key={status}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getStatusIcon(status)}
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {status === 'open' ? 'inProgress' : status}
                      </Typography>
                </Box>
                        <Typography variant="body2" fontWeight={500}>
                          {count} ({Math.round((count / stats.totalTickets) * 100) || 0}%)
                      </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={Math.round((count / stats.totalTickets) * 100) || 0} 
                        sx={{ 
                          height: 8, 
                          borderRadius: 4,
                          bgcolor: 'rgba(0, 0, 0, 0.05)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: getStatusColor(status)
                          }
                        }}
                      />
                </Box>
                  )
                ))}
              </Stack>
            </Paper>
                </Box>
                
          {/* Tickets by priority */}
          <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', md: 'calc(33% - 2rem)' } }}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 3,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <PriorityHighIcon />
                {getTranslationString('dashboard.admin.priorityDistribution', 'Répartition par priorité')}
                      </Typography>
              
              <Stack spacing={2}>
                {Object.entries(stats.ticketsByPriority).map(([priority, count]) => (
                  count > 0 && (
                    <Box key={priority}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                          {priority === 'high' ? 'critical' : priority}
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {count} ({getPriorityPercentage(priority as 'low' | 'medium' | 'high')}%)
                </Typography>
                </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={getPriorityPercentage(priority as 'low' | 'medium' | 'high')} 
                        sx={{ 
                          height: 8, 
                          borderRadius: 4,
                          bgcolor: 'rgba(0, 0, 0, 0.05)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: getPriorityColor(priority)
                          }
                        }}
                      />
              </Box>
                  )
                ))}
              </Stack>
            </Paper>
          </Box>
          
          {/* Tickets by category */}
          <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', md: 'calc(33% - 2rem)' } }}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 3,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <CategoryIcon />
                {getTranslationString('dashboard.admin.categoryDistribution', 'Répartition par catégorie')}
              </Typography>
              
              <Stack spacing={2}>
                {Object.entries(stats.ticketsByCategory).map(([category, count]) => (
                  count > 0 && (
                    <Box key={category}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getCategoryIcon(category)}
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {category}
                          </Typography>
                        </Box>
                        <Typography variant="body2" fontWeight={500}>
                          {count} ({Math.round((count / stats.totalTickets) * 100) || 0}%)
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={Math.round((count / stats.totalTickets) * 100) || 0} 
                        sx={{ 
                          height: 8, 
                          borderRadius: 4,
                          bgcolor: 'rgba(0, 0, 0, 0.05)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: getCategoryColor(category)
                          }
                        }}
                      />
                    </Box>
                  )
                ))}
              </Stack>
            </Paper>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {/* Admin Tools Section */}
          <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', md: 'calc(66.666% - 1.5rem)' } }}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                height: '100%'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <SupervisorAccountIcon />
                {getTranslationString('dashboard.admin.adminTools', 'Admin Tools')}
              </Typography>
                <Chip
                  icon={<GroupIcon />}
                  label={`${stats.activeUsers} ${getTranslationString('dashboard.admin.activeUsers', 'utilisateurs actifs')}`}
                  color="primary"
                  size="small"
                  variant="outlined"
                />
              </Box>
              
              <List>
                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/admin/control-access">
                    <ListItemIcon>
                      <GroupIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={getTranslationString('dashboard.admin.userManagement', 'User Management')} 
                      secondary={getTranslationString('dashboard.admin.userManagementDesc', 'Manage users and permissions')}
                    />
                  </ListItemButton>
                </ListItem>
                <Divider component="li" />
                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/admin/settings">
                    <ListItemIcon>
                      <SettingsIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={getTranslationString('dashboard.admin.systemConfig', 'System Configuration')} 
                      secondary={getTranslationString('dashboard.admin.systemConfigDesc', 'Configure system settings')}
                    />
                  </ListItemButton>
                </ListItem>
                <Divider component="li" />
                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/admin/analytics">
                    <ListItemIcon>
                      <BarChartIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={getTranslationString('dashboard.admin.analytics', 'Analytics & Reports')} 
                      secondary={getTranslationString('dashboard.admin.analyticsDesc', 'View system analytics')}
                    />
                  </ListItemButton>
                </ListItem>
                <Divider component="li" />
                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/tickets">
                    <ListItemIcon>
                      <ConfirmationNumberIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary={getTranslationString('dashboard.admin.ticketManager', 'Ticket Manager')} 
                      secondary={`${stats.pendingTickets} ${getTranslationString('dashboard.admin.ticketsInProgress', 'tickets en cours')}`}
                    />
                  </ListItemButton>
                </ListItem>
              </List>
            </Paper>
          </Box>
          
          {/* Customer Overview Section */}
          <Box sx={{ flexGrow: 1, flexBasis: { xs: '100%', md: 'calc(33.333% - 1.5rem)' } }}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <BusinessIcon />
                  {getTranslationString('dashboard.admin.customerOverview', 'Aperçu clients')}
                </Typography>
                <Chip
                  icon={<BusinessIcon />}
                  label={`${stats.totalCustomers} ${getTranslationString('dashboard.admin.companies', 'entreprises')}`}
                  color="secondary"
                  size="small"
                  variant="outlined"
                />
              </Box>
              
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                {loadingCustomers ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress size={40} />
                  </Box>
                ) : customers.length > 0 ? (
                  <Stack spacing={2}>
                    {customers.slice(0, 3).map((customer) => (
                      <Box 
                        key={customer.id} 
                        sx={{ 
                          p: 1.5, 
                          borderRadius: 2, 
                          bgcolor: 'rgba(0, 0, 0, 0.02)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5
                        }}
                      >
                        <Avatar 
                          src={customer.logoUrl} 
                          sx={{ 
                            width: 40, 
                            height: 40, 
                            bgcolor: theme.palette.secondary.main 
                          }}
                        >
                          {customer.companyName?.charAt(0) || <BusinessIcon />}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={600} noWrap>
                            {customer.companyName || customer.name || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {customer.email || getTranslationString('common.noEmail', 'Sans email')} • {customer.userIds?.length || 0} {getTranslationString('common.users', 'utilisateurs')}
                          </Typography>
                        </Box>
                        <Chip 
                          size="small" 
                          color={customer.status === 'active' ? 'success' : 'error'} 
                          label={customer.status === 'active' 
                            ? getTranslationString('customers.status.active', 'Actif') 
                            : getTranslationString('customers.status.inactive', 'Inactif')} 
                          variant="outlined"
                        />
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" textAlign="center" color="text.secondary">
                    {getTranslationString('dashboard.admin.noCustomers', 'Aucun client disponible')}
                  </Typography>
                )}
              </Box>
              
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                  variant="outlined" 
                  color="secondary"
                  component={Link}
                  to="/admin/control-access"
                  state={{ defaultTab: 2 }}
                  endIcon={<ArrowForwardIcon />}
                  size="small"
                >
                  {getTranslationString('dashboard.admin.viewAllCustomers', 'Voir tous les clients')}
                </Button>
              </Box>
            </Paper>
          </Box>
        </Box>
        
        {/* Recent Users and Customers Section */}
        <Box sx={{ mt: 3 }}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 3, 
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <GroupIcon />
                {getTranslationString('dashboard.admin.recentUsers', 'Utilisateurs récents')}
              </Typography>
              <Button 
                variant="outlined" 
                size="small"
                component={Link}
                to="/admin/control-access"
                endIcon={<ArrowForwardIcon />}
              >
                {getTranslationString('dashboard.admin.manageAllUsers', 'Gérer les utilisateurs')}
              </Button>
            </Box>
            
            {loadingUsers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Table size="medium">
                  <TableHead sx={{ bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Utilisateur</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Rôle</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Création</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Statut</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow 
                        key={user.id}
                        sx={{ 
                          '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                          borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
                              {user.displayName?.charAt(0) || <PersonIcon fontSize="small" />}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {user.displayName || 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{user.email || 'N/A'}</TableCell>
                        <TableCell>
                          {user.roleId === 1 ? (
                            <Chip 
                              size="small" 
                              color="primary" 
                              label="Admin" 
                              icon={<AdminPanelSettingsIcon fontSize="small" />}
                            />
                          ) : user.roleId === 2 ? (
                            <Chip 
                              size="small" 
                              color="info" 
                              label="Client" 
                              icon={<PersonIcon fontSize="small" />}
                            />
                          ) : user.roleId === 3 ? (
                            <Chip 
                              size="small" 
                              color="secondary" 
                              label="Agent" 
                              icon={<SupervisorAccountIcon fontSize="small" />}
                            />
                          ) : (
                            <Chip 
                              size="small" 
                              color="default" 
                              label="Unknown" 
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AccessTimeIcon fontSize="small" color="action" />
                            <Typography variant="caption">
                              {formatDate(user.createdAt)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {user.isDisabled ? (
                            <Chip 
                              size="small" 
                              color="error" 
                              label={getTranslationString('users.status.disabled', 'Disabled')} 
                              icon={<NoAccountsIcon fontSize="small" />}
                            />
                          ) : (
                            <Chip 
                              size="small" 
                              color="success" 
                              label={getTranslationString('users.status.active', 'Active')} 
                              icon={<VerifiedUserIcon fontSize="small" />}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {users.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                          {getTranslationString('common.noData', 'No data available')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Box>
        
        <GlobalStyles />
      </Container>
    </Box>
  );
};

export default DashboardAdmin; 